import {defineStore} from 'pinia';
import type {RegionDecorator} from '@/types/region.decorator';
import {type Ref, ref} from 'vue';
import {fetchRegions} from '@/api/api';
import {getLocalStorageParam, getUrlParam, updateUrlParam} from '@/utils/url.param.utils';
import {UrlParam} from '@/utils/url.param';
import {useAppStore} from '@/store/app.store';

const DEFAULT_FALLBACK_REGION = 'netherlands';

export const useRegionStore = defineStore('regionStore', () => {

	const initialRegion = () => {
		// Get region from URL or if not available get user's default or get from localstorage.
		const region = getUrlParam(UrlParam.REGION_KEY) || useAppStore().getPrimaryUserRegion() || getLocalStorageParam(UrlParam.REGION_KEY);

		updateUrlParam(UrlParam.REGION_KEY, region);
		return region;
	};

	const regions: Ref<Array<RegionDecorator>> = ref([]);
	const selectedRegion: Ref<string | null> = ref(initialRegion());

	const setSelectedRegion = (newRegion: RegionDecorator) => {
		selectedRegion.value = newRegion?.urlName;
		updateUrlParam(UrlParam.REGION_KEY, selectedRegion.value);
	};

	const getSelectedRegion = () => {
		const foundRegion = regions.value?.find((region: RegionDecorator) => region.urlName === selectedRegion.value);
		return foundRegion ?? regions.value[0];
	};

	const getSelectedRegionUrlName = () => getSelectedRegion()?.urlName;

	const setRegions = (availableRegions: RegionDecorator[]) => regions.value = availableRegions;

	const loadRegions = async () => {
		if (regions.value?.length > 0) {
			return;
		}

		const data = await fetchRegions(selectedRegion.value);

		if (data?.length > 0) {
			setRegions(data);

			if (!selectedRegion.value) {
				const activeRegion = regions.value.find((region: RegionDecorator) => region.active);
				const fallbackRegion = regions.value.find((region: RegionDecorator) => region.urlName === DEFAULT_FALLBACK_REGION);
				const defaultRegion = fallbackRegion ?? regions.value[0];
				setSelectedRegion(activeRegion ?? defaultRegion);
			}
		} else {
			console.error('Error fetching regions');
		}
	};

	return {
		regions,
		loadRegions,
		getSelectedRegion,
		getSelectedRegionUrlName,
		setSelectedRegion
	};
});
